import {
  Button,
  Card,
  Group,
  LoadingOverlay,
  Pagination,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { IconCategory, IconPlus, IconSearch } from "@tabler/icons-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  ListError,
  ListHeader,
  ListScroll,
  ListSkeleton,
} from "@/components/DataList";
import { useAccess } from "@/hooks/useAccess";
import { lockProps } from "@/lib/permissions";
import {
  deleteCategory,
  getCategories,
  removeCategoryImage,
  updateCategory,
  updateCategoryRank,
  uploadCategoryImage,
} from "@/api/category";
import {
  getBranchCategoryOverrides,
  getBranches,
  upsertBranchCategoryOverride,
} from "@/api/branches";
import { getProducts } from "@/api/products";
import { notify } from "@/lib/notify";
import { PER_PAGE_OPTIONS } from "@/lib/format";
import { ImageUploadModal } from "@/components/ImageUploadModal";
import { categoryTarget } from "@/lib/imageTargets";
import { PageTitle } from "@/components/PageTitle";
import { SortableCategoryRow } from "./SortableCategoryRow";
import { CategoryFormModal } from "./CategoryFormModal";
import { EMPTY_LIST } from "@/lib/queryClient";

// Başlık ve yükleme iskeleti aynı yerden besleniyor; genişlikler SortableCategoryRow'daki
// hücrelerle birebir örtüşmek zorunda, yoksa kolonlar kayar.
const CATEGORY_COLUMNS = [
  { spacer: true, w: 28 },
  { spacer: true, w: 40 },
  { label: "Kategori Adı", flex: true, ta: "left" },
  { label: "İçerik", w: 70 },
  { label: "Durum", w: 44 },
  { label: "Şube", w: 90 },
  { spacer: true, w: 16 },
  { label: "İşlem", w: 108 },
];

export default function Categories() {
  const { can } = useAccess();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["categories"],
    queryFn: getCategories,
  });
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [formOpened, { open: openForm, close: closeForm }] =
    useDisclosure(false);
  const [formCategory, setFormCategory] = useState(null);
  const [imageOpened, { open: openImage, close: closeImage }] =
    useDisclosure(false);
  const [imageCategory, setImageCategory] = useState(null);

  useEffect(() => {
    if (data) {
      setItems([...data].sort((a, b) => a.rank - b.rank));
    }
  }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const rankMutation = useMutation({
    mutationFn: updateCategoryRank,
    onSuccess: () => notify.success("Sıralama kaydedildi"),
    onError: () =>
      notify.error("Sıralama kaydedilemedi — liste eski sırasına döndü"),
    // Sıra yalnızca local state'te duruyordu: cache eski rank'leri tutmaya devam ettiği
    // için sayfadan çıkıp 30sn içinde dönünce kaydedilen sıra geri alınmış gibi görünürdü.
    // Hata durumunda da tazeliyoruz; DB'den gelen sıra UI'daki yalanı düzeltir.
    onSettled: () =>
      void queryClient.invalidateQueries({ queryKey: ["categories"] }),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }) => updateCategory(id, { isActive }),
    onSuccess: (_data, { name, isActive }) => {
      notify.success(`${name} ${isActive ? "aktif edildi" : "pasife alındı"}`);
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
    // Switch iyimser olarak çevrildi; istek patlarsa geri alınmazsa kategori kapatıldı sanılır
    // ama menüde görünmeye devam eder.
    onError: (_error, { id, isActive }) => {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isActive: !isActive } : item,
        ),
      );
      notify.error("Durum güncellenemedi");
    },
  });

  // Kategori satırında kaç ürün olduğunu göstermek için; ürün listesi zaten önbellekte.
  const { data: products = EMPTY_LIST } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });
  const productCountByCategoryId = useMemo(() => {
    const counts = new Map();

    for (const product of products) {
      counts.set(product.categoryId, (counts.get(product.categoryId) ?? 0) + 1);
    }

    return counts;
  }, [products]);

  const { data: branches = EMPTY_LIST } = useQuery({
    queryKey: ["branches"],
    queryFn: getBranches,
  });
  const [togglingBranchId, setTogglingBranchId] = useState(null);

  const categoryOverrideQueries = useQueries({
    queries: branches.map((branch) => ({
      queryKey: ["branch-category-overrides", branch.id],
      queryFn: () => getBranchCategoryOverrides(branch.id),
    })),
  });

  const overridesByBranch = useMemo(() => {
    const map = {};
    branches.forEach((branch, index) => {
      map[branch.id] = categoryOverrideQueries[index]?.data ?? [];
    });
    return map;
  }, [branches, categoryOverrideQueries]);

  const categoryBranchVisible = (categoryId, branchId) =>
    overridesByBranch[branchId]?.find((item) => item.categoryId === categoryId)
      ?.isActive !== false;

  const branchToggleMutation = useMutation({
    mutationFn: ({ branchId, categoryId, visible }) =>
      upsertBranchCategoryOverride(branchId, categoryId, { isActive: visible }),
    onSuccess: (_data, { category, branchId, visible }) => {
      const branchName =
        branches.find((branch) => branch.id === branchId)?.name ?? "";
      notify.success(
        `${branchName} şubesinde ${category.name} ${visible ? "açıldı" : "kapatıldı"}`,
      );
      queryClient.invalidateQueries({
        queryKey: ["branch-category-overrides", branchId],
      });
    },
    onError: () => notify.error("Şube durumu güncellenemedi"),
    onSettled: () => setTogglingBranchId(null),
  });

  const handleToggleBranch = (category, branchId, visible) => {
    setTogglingBranchId(branchId);
    branchToggleMutation.mutate({
      category,
      branchId,
      categoryId: category.id,
      visible,
    });
  };

  // Şube başına ayrı istek: biri düşerse diğerleri yine de yazılır. Promise.all ilk hatada
  // reddedip "hiçbiri olmadı" derdi, oysa bir kısmı yazılmış oluyordu.
  const allBranchesMutation = useMutation({
    mutationFn: async ({ category, visible }) => {
      const results = await Promise.allSettled(
        branches.map((branch) =>
          upsertBranchCategoryOverride(branch.id, category.id, {
            isActive: visible,
          }),
        ),
      );

      return {
        failed: results.filter((item) => item.status === "rejected").length,
        total: results.length,
      };
    },
    onSuccess: ({ failed, total }, { category, visible }) => {
      const verb = visible ? "açıldı" : "kapatıldı";

      if (failed) {
        notify.error(
          `${category.name}: ${total - failed}/${total} şubede ${verb}, ${failed} şube başarısız`,
        );
        return;
      }
      notify.success(`${category.name} tüm şubelerde ${verb}`);
    },
    onError: () => notify.error("Şube durumları güncellenemedi"),
    // Kısmi başarıda da tazele: yazılan şubelerin cache'i güncellenmezse UI eski hali gösterir.
    onSettled: () =>
      branches.forEach((branch) =>
        queryClient.invalidateQueries({
          queryKey: ["branch-category-overrides", branch.id],
        }),
      ),
  });

  const handleToggleAllBranches = (category, visible) =>
    allBranchesMutation.mutate({ category, visible });

  const deleteMutation = useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      notify.success("Kategori silindi");
      queryClient.invalidateQueries({ queryKey: ["categories"] });
    },
    onError: (error) =>
      notify.error(error.response?.data?.error ?? "Kategori silinemedi"),
  });

  const sortable = !search.trim();

  const handleDragEnd = ({ active, over }) => {
    if (!sortable || !over || active.id === over.id) {
      return;
    }
    const oldIndex = items.findIndex((category) => category.id === active.id);
    const newIndex = items.findIndex((category) => category.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);
    rankMutation.mutate(
      reordered.map((category, index) => ({ id: category.id, rank: index })),
    );
  };

  const handleToggle = (category, isActive) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === category.id ? { ...item, isActive } : item,
      ),
    );
    toggleMutation.mutate({ id: category.id, isActive, name: category.name });
  };

  const handleDelete = (category) =>
    modals.openConfirmModal({
      title: "Kategoriyi sil",
      centered: true,
      children: (
        <Text size="sm">
          {category.name} kategorisini silmek istediğine emin misin?
        </Text>
      ),
      labels: { confirm: "Sil", cancel: "Vazgeç" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteMutation.mutate(category.id),
    });

  const handleEdit = (category) => {
    setFormCategory(category);
    openForm();
  };

  const handleNew = () => {
    setFormCategory(null);
    openForm();
  };

  const handleEditImage = (category) => {
    setImageCategory(category);
    openImage();
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return items;
    }
    return items.filter((category) =>
      category.name.toLowerCase().includes(term),
    );
  }, [items, search]);

  useEffect(() => {
    setPage(1);
  }, [search, perPage]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <Stack gap="lg">
      <PageTitle>Kategoriler</PageTitle>
      <Group justify="space-between">
        <Group>
          <ThemeIcon size={46} radius="md" variant="light" color="chocolate">
            <IconCategory size={26} stroke={1.6} />
          </ThemeIcon>
          <div>
            <Title order={2}>Kategoriler</Title>
            <Text c="dimmed" size="sm">
              Sürükleyerek sırala, anahtarla aktif/pasif yap
            </Text>
          </div>
        </Group>
        {/* Yetkisiz kullanıcıda gizlenmiyor, kilitleniyor. */}
        <Button
          leftSection={<IconPlus size={16} />}
          {...lockProps(can("categories"))}
          onClick={handleNew}
        >
          Yeni Kategori
        </Button>
      </Group>

      <Card withBorder radius="md" p="md">
        <Group justify="center" mb="md">
          <TextInput
            w={260}
            placeholder="Kategori adı ara"
            leftSection={<IconSearch size={15} />}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </Group>

        <Card withBorder radius="md" p={0} pos="relative">
          <LoadingOverlay
            visible={rankMutation.isPending}
            zIndex={5}
            overlayProps={{ blur: 1, backgroundOpacity: 0.35 }}
            loaderProps={{ size: "sm" }}
          />
          {isLoading ? (
            <ListScroll minWidth={596}>
              <ListHeader columns={CATEGORY_COLUMNS} />
              <ListSkeleton
                columns={CATEGORY_COLUMNS}
                rows={perPage > 10 ? 10 : perPage}
              />
            </ListScroll>
          ) : isError ? (
            <ListError title="Kategoriler alınamadı" onRetry={refetch} />
          ) : (
            <>
              <ListScroll minWidth={596}>
                <ListHeader columns={CATEGORY_COLUMNS} />

                {pageItems.length === 0 ? (
                  <Text c="dimmed" ta="center" py="xl">
                    Kategori bulunamadı
                  </Text>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={pageItems.map((category) => category.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {pageItems.map((category) => (
                        <SortableCategoryRow
                          key={category.id}
                          category={category}
                          productCount={
                            productCountByCategoryId.get(category.id) ?? 0
                          }
                          sortable={sortable}
                          // Switch iyimser çevriliyor — spinner yerine
                          // yalnızca kilitlenir.
                          toggling={
                            toggleMutation.isPending &&
                            toggleMutation.variables?.id === category.id
                          }
                          onToggle={handleToggle}
                          onEdit={handleEdit}
                          onEditImage={handleEditImage}
                          onDelete={handleDelete}
                          branches={branches}
                          branchVisible={categoryBranchVisible}
                          onToggleBranch={handleToggleBranch}
                          onToggleAllBranches={handleToggleAllBranches}
                          togglingBranchId={togglingBranchId}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </ListScroll>

              <Group justify="space-between" p="sm">
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    Sayfa başı
                  </Text>
                  <Select
                    withCheckIcon={false}
                    w={75}
                    size="xs"
                    data={PER_PAGE_OPTIONS}
                    value={String(perPage)}
                    onChange={(value) => {
                      setPerPage(Number(value));
                      setPage(1);
                    }}
                  />
                  <Text size="sm" c="dimmed">
                    {filtered.length} kategori
                  </Text>
                </Group>
                <Pagination
                  withEdges
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                  size="sm"
                />
              </Group>
            </>
          )}
        </Card>
      </Card>

      <CategoryFormModal
        opened={formOpened}
        onClose={closeForm}
        category={formCategory}
      />
      <ImageUploadModal
        opened={imageOpened}
        onClose={closeImage}
        title="Kategori Görseli"
        idField="id"
        idValue={imageCategory?.id}
        uploadFn={uploadCategoryImage}
        currentImageUrl={imageCategory?.imageUrl}
        removeFn={removeCategoryImage}
        invalidateKey={["categories"]}
        // Kadraj çerçevesi kategorinin görünüm sırasına göre: aynı ızgarada altı farklı
        // karo şekli var ve tek bir orana kadrajlamak dördünde yanlış çerçeve üretiyordu.
        crop={categoryTarget(imageCategory?.view)}
      />
    </Stack>
  );
}
